import { inject, Injectable, signal } from '@angular/core';
import { AccountCompanyService } from './account-company.service';
import { AccountCompanyWiseConfigService } from './account-company-wise-config.service';
import { AccountCompanyDto } from '../models/dtos/account-company.dto';
@Injectable({ providedIn: 'root' })
export class AccountingCompanyContextService {
  private readonly companyService = inject(AccountCompanyService);

  private readonly companyWiseConfigService = inject(
    AccountCompanyWiseConfigService,
  );

  readonly company = signal<AccountCompanyDto | null>(null);
  readonly isLoading = signal(false);

  readonly switchGeneration = signal(0);
  readonly changeCompanyModalOpen = signal(false);
  readonly changeCompanyModalMode = signal<'normal' | 'login'>('normal');
  readonly loginCompanySelectionPending = signal(false);
  readonly loginCompanyReturnUrl = signal<string | null>(null);
  private loadInFlight = false;

  ensureLoaded(force = false): void {
    if (!force && !this.companyService.isCompanyOpen()) return;
    if (!force && this.company()) return;
    if (!force && this.loadInFlight) return;
    this.loadInFlight = true;
    this.isLoading.set(true);
    const id = this.companyService.getActiveCompanyId();
    this.companyService.getCompanyById(id).subscribe({
      next: (res) => {
        this.loadInFlight = false;
        this.isLoading.set(false);
        if (res.success && res.data) {
          this.applyCompany(res.data, true);
          return;        }

        this.loadDefaultFallback();
      },

      error: () => {
        this.loadInFlight = false;
        this.isLoading.set(false);
        this.loadDefaultFallback();
      },
    });
  }

  refresh(): void {
    if (!this.companyService.isCompanyOpen()) return;
    this.ensureLoaded(true);
  }

  openChangeCompanyModal(): void {
    this.changeCompanyModalMode.set('normal');
    this.changeCompanyModalOpen.set(true);
  }

  openLoginCompanySelectionModal(returnUrl?: string | null): void {
    this.company.set(null);
    this.changeCompanyModalMode.set('login');
    this.loginCompanySelectionPending.set(true);
    this.loginCompanyReturnUrl.set(this.normalizeReturnUrl(returnUrl));
    this.changeCompanyModalOpen.set(true);
  }

  completeLoginCompanySelection(): void {
    this.loginCompanySelectionPending.set(false);
    this.changeCompanyModalMode.set('normal');
    this.loginCompanyReturnUrl.set(null);
    this.changeCompanyModalOpen.set(false);
  }

  clearLoginCompanySelection(): void {
    this.loginCompanySelectionPending.set(false);
    this.changeCompanyModalMode.set('normal');
    this.loginCompanyReturnUrl.set(null);
    this.changeCompanyModalOpen.set(false);
  }

  closeChangeCompanyModal(): void {
    if (this.changeCompanyModalMode() === 'login') {
      return;
    }
    this.changeCompanyModalOpen.set(false);
  }

  private normalizeReturnUrl(returnUrl?: string | null): string | null {
    if (
      returnUrl &&
      returnUrl.startsWith('/') &&
      !returnUrl.startsWith('/login') &&
      !returnUrl.startsWith('/select-company')
    ) {
      return returnUrl;
    }
    return null;
  }

  /** Switches active company; returns true when the company id changed. */
  switchToCompany(dto: AccountCompanyDto): boolean {
    const next = (dto.accountCompanyId ?? '').trim().toLowerCase();
    if (!next) return false;

    // Compare against the in-memory signal (UI source of truth), not storage.
    // Callers may set the active id in storage before invoking this method.
    const prev = (this.company()?.accountCompanyId ?? '').trim().toLowerCase();
    const changed = prev !== next;

    if (changed) {
      this.companyWiseConfigService.clearCache();
    }

    this.applyCompany(dto, true);

    if (changed) {
      this.switchGeneration.update((n) => n + 1);
    }

    return changed;
  }

  setActiveCompany(dto: AccountCompanyDto): void {
    this.switchToCompany(dto);
  }

  shutCompany(): void {
    this.companyService.shutCompany();
    this.companyWiseConfigService.clearCache();
    this.company.set(null);
  }

  get companyName(): string {
    return this.company()?.companyName?.trim() || 'Company';
  }

  /** AccountCompanies.BooksBeginningFrom (ISO date), when company is loaded. */
  get booksBeginningFrom(): string {
    const raw = this.company()?.booksBeginningFrom?.trim() ?? '';
    return raw.length >= 10 ? raw.slice(0, 10) : '';
  }

  private applyCompany(dto: AccountCompanyDto, preloadConfig: boolean): void {
    if (dto.accountCompanyId) {
      this.companyService.setActiveCompanyId(dto.accountCompanyId);
    }

    this.companyService.markCompanyOpen();
    this.company.set(dto);

    if (preloadConfig) {
      this.companyWiseConfigService.loadForActiveCompany().subscribe();
    }
  }

  private loadDefaultFallback(): void {
    this.companyService.getDefaultCompany().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.applyCompany(res.data, true);
        }
      },
    });
  }
}
